import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { CheckCircle2, XCircle, ShieldCheck } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import DigitalIDCard from "@/components/DigitalIDCard";

export default function Verify() {
  const { membershipId } = useParams();
  const [state, setState] = useState({ loading: true, data: null });

  useEffect(() => {
    api.get(`/verify/${membershipId}`)
      .then(r => setState({ loading: false, data: r.data }))
      .catch(() => setState({ loading: false, data: { verified: false, message: "Verification failed" } }));
  }, [membershipId]);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-3xl w-full mx-auto px-4 py-12">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}>
          <div className="text-center mb-8">
            <Badge variant="outline" className="mb-3">QR Verification</Badge>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Membership Verification</h1>
            <p className="text-muted-foreground mt-2 font-mono">{membershipId}</p>
          </div>

          {state.loading ? (
            <Card className="p-12 text-center text-muted-foreground">Verifying…</Card>
          ) : state.data?.verified ? (
            <div className="space-y-6">
              <Card className="p-6 border-2 border-brand-green/40 bg-brand-green/5">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-brand-green text-white flex items-center justify-center">
                    <CheckCircle2 className="w-8 h-8" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-brand-green font-bold">Verified Member</div>
                    <h2 className="text-2xl font-black tracking-tight" data-testid="verify-name">{state.data.full_name}</h2>
                    <div className="text-sm text-muted-foreground">{state.data.designation} • {state.data.district}</div>
                  </div>
                </div>
                <div className="grid sm:grid-cols-3 gap-4 mt-6">
                  <Stat k="Membership ID" v={state.data.membership_id} />
                  <Stat k="Status" v={(state.data.membership_status || "active").toUpperCase()} />
                  <Stat k="Valid till" v={(state.data.expiry_date || "—").slice(0, 10)} />
                </div>
              </Card>
              <div className="flex justify-center">
                <DigitalIDCard member={state.data} />
              </div>
            </div>
          ) : (
            <Card className="p-10 text-center border-2 border-destructive/40 bg-destructive/5" data-testid="verify-failed">
              <XCircle className="w-14 h-14 text-destructive mx-auto" />
              <h2 className="text-2xl font-black mt-4">Not a verified member</h2>
              <p className="text-sm text-muted-foreground mt-2">
                {state.data?.message || "This membership ID could not be verified."}
              </p>
            </Card>
          )}

          <div className="mt-10 text-center text-xs text-muted-foreground">
            <ShieldCheck className="w-4 h-4 inline mr-1" />
            This page is a public, tamper-proof KELTA verification endpoint.
          </div>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
}

function Stat({ k, v }) {
  return (
    <div className="rounded-md bg-card border border-border p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{k}</div>
      <div className="font-mono font-bold text-sm mt-0.5">{v}</div>
    </div>
  );
}
